import { Suspense, useContext, useEffect, useRef, useState } from "react"
import { ConnectionHandler, readInlineData, useFragment } from "react-relay"
import { Outlet, useParams, useSearchParams } from "react-router"
import { faro } from "@grafana/faro-react"
import { usePrevious } from "@uidotdev/usehooks"
import { commitLocalUpdate } from "relay-runtime"
import { useUser } from "@/apis/bff"
import { AppInsights } from "@/app-insights"
import { UpdateVersionBanner } from "@/components/update-version-banner"
import { UserWarningBanner } from "@/components/user-warning-banner"
import { PatientLayoutProvider } from "@/contexts/patient-layout-context"
import { useConfirmBeforeLeaving } from "@/hooks/useConfirmBeforeLeaving"
import { getInsensitiveSearchParams } from "@/lib/get-insensitive-search-params"
import { cn } from "@/lib/utils"
import { getChatRoute, useIsChatLocation } from "@/modules/chats/lib/routing-helpers"
import { RequestTakeOverDialog } from "@/modules/common/dialog/request-take-over-dialog"
import { DuplicateWarningBanner } from "@/modules/patients/components/duplicate-warning-banner"
import { PatientHeader } from "@/modules/patients/components/patient-header.tsx"
import { TeleGpOnboardingBanner } from "@/modules/patients/components/tele-gp-onboarding-banner"
import { HealthInformationCopilotProvider } from "@/modules/patients/contexts/health-information-copilot-context"
import { BetterDocSuggestionsStoreProvider } from "@/modules/patients/detail/better-doc/store/useBetterDocSuggestionsStore"
import {
  CloseMedialRecordConfirmationDialog,
  CloseOutput,
} from "@/modules/patients/detail/close-medial-record-confirmation-dialog"
import { ConsultationReportStoreProvider } from "@/modules/patients/detail/consultation-report/store/useConsultationReportStore"
import { GeneralAttestationProvider } from "@/modules/patients/detail/general-attestation/store/useGeneralAttestationStore"
import { LegalGuardianAttestationDrawerProvider } from "@/modules/patients/detail/legal-guardian-attestation/store/useLegalGuardianAttestationDrawerStore"
import { ChMedicationProvider } from "@/modules/patients/detail/medication/store/useChMedicationStore"
import { DeMedicationProvider } from "@/modules/patients/detail/medication/store/useDeMedicationStore"
import { PatientNavbar } from "@/modules/patients/detail/patient-navbar"
import { ChPrescriptionStoreProvider } from "@/modules/patients/detail/prescription/ch/store/usePrescriptionStore"
import { DePrescriptionStoreProvider } from "@/modules/patients/detail/prescription/de/store/usePrescriptionStore"
import { ProgressNoteFormProvider } from "@/modules/patients/detail/progress-note/hooks/useProgressNoteFormStore"
import { getProgressNoteKind } from "@/modules/patients/detail/progress-note/lib/get-progress-note-kind"
import { PointOfCareProvider } from "@/modules/patients/detail/progress-note/point-of-care/point-of-care-context"
import { ReferralStoreProvider } from "@/modules/patients/detail/referral/store/useReferralStore"
import { SharedMedicalRecordNote } from "@/modules/patients/detail/shared-medical-record-note"
import { useMedicalRecordDialogStore } from "@/modules/patients/detail/store/useCloseMedicalRecordDialogStore"
import {
  ConcludeDialogStoreProvider,
  useConcludeDialogStoreContext,
} from "@/modules/patients/detail/store/useConcludeDialogStore"
import { ConfirmDialogStoreProvider } from "@/modules/patients/detail/store/useConfirmDialogStore"
import { EditEpisodeOfCareDialogStoreProvider } from "@/modules/patients/detail/store/useEpisodeOfCareDialogStore"
import { EpisodeOfCareStoreProvider } from "@/modules/patients/detail/store/useEpisodeOfCareStore"
import { PatientFileProvider } from "@/modules/patients/detail/store/usePatientStore"
import { PreventionProgramsProvider } from "@/modules/patients/detail/store/usePreventionProgramsStore"
import { PsychologicalPsytherapyStoreProvider } from "@/modules/patients/detail/therapy-prescription/ch/store/usePsychologicalPsytherapyStore"
import { TherapyDrawerStoreProvider } from "@/modules/patients/detail/therapy-prescription/store/useTherapyDrawerStore"
import { useAppointmentParam } from "@/modules/patients/hooks/useAppointmentParam"
import { useContextAppointment } from "@/modules/patients/hooks/useContextAppointment"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"
import { useEmptyEpisodesOfCareData } from "@/modules/patients/hooks/useEmptyEpisodesOfCareData"
import { usePatientEpisodesOfCare } from "@/modules/patients/hooks/usePatientEpisodesOfCare"
import { usePatientParam } from "@/modules/patients/hooks/usePatientParam"
import { usePreviewParam } from "@/modules/patients/hooks/usePreviewParam"
import { useProgressNoteData } from "@/modules/patients/hooks/useProgressNoteData"
import { CaveWarning } from "@/modules/patients/information/cave/cave-warning.tsx"
import { PatientFileLockFragmentInline$key } from "@/modules/patients/services/__generated__/PatientFileLockFragmentInline.graphql"
import { PatientFragment$key } from "@/modules/patients/services/__generated__/PatientFragment.graphql.ts"
import { PatientFragmentInline$key } from "@/modules/patients/services/__generated__/PatientFragmentInline.graphql"
import { chPatientByIdQuery$data } from "@/modules/patients/services/ch/__generated__/chPatientByIdQuery.graphql"
import { dePatientByIdQuery$data } from "@/modules/patients/services/de/__generated__/dePatientByIdQuery.graphql"
import {
  fetchTreatmentPlanTemplates,
  TreatmentPlanTemplatesStoreContext,
  TreatmentPlanTemplatesStoreProvider,
} from "@/modules/patients/services/fetchTreatmentPlanTemplates"
import { PatientFileLockFragmentInline } from "@/modules/patients/services/PatientFileLockFragment"
import { PatientFragment, PatientFragmentInline } from "@/modules/patients/services/PatientFragment.ts"
import { useCancelAppointmentMutation } from "@/modules/patients/services/useCancelAppointmentMutation"
import { useDeleteEpisodesOfCare } from "@/modules/patients/services/useDeleteEpisodesOfCare"
import { useDeleteProgressNotes } from "@/modules/patients/services/useDeleteProgressNotes"
import { useMedicalReportAttachmentSubscription } from "@/modules/patients/services/useMedicalReportAttachmentSubscription"
import { usePatientByIdQuery } from "@/modules/patients/services/usePatientByIdQuery.ts"
import { useReleasePatientFileLockMutation } from "@/modules/patients/services/useReleasePatientFileLockMutation"
import { PatientParamProvider } from "@/modules/patients/stores/PatientParamProvider"
import { useSharedMedicalRecordNoteStore } from "@/modules/patients/stores/useSharedMedicalRecordNoteStore"
import { CallLineBanner } from "@/modules/telephony/call-line-banner.tsx"
import { PhoneCallTranscriptionBarContainer } from "@/modules/telephony/components/phone-call-transcription-bar-container"
import { Notes } from "@/modules/temporary-notes/notes.tsx"
import { RelayEnvironment } from "@/relay-environment"
import { SofiaEnvironment } from "@/sofia-environment"
import useCallContextStore from "@/stores/useCallContextStore"
import { CareProviderManualAddressStoreProvider } from "@/stores/useCareProviderManualAddressStore"
import { useDrawerStore } from "@/stores/useDrawerStore"
import { PhoneCallTranscriptionStoreProvider } from "@/stores/usePhoneCallTranscriptionStore"
import { Country } from "@/types/__generated__/union-types"

const getIsEditMode = (
  userName: string,
  patientRef: dePatientByIdQuery$data | chPatientByIdQuery$data,
  appointmentId: string | null,
  isPreview: boolean,
  isDuplicate: boolean,
) => {
  const countryPatient = patientRef.patientById
  const patient = readInlineData<PatientFragmentInline$key>(PatientFragmentInline, countryPatient)
  const patientFileLock = readInlineData<PatientFileLockFragmentInline$key>(
    PatientFileLockFragmentInline,
    patient?.patientFileLock ?? null,
  )

  if (isPreview || isDuplicate) return { isStartEditingDisplayed: false, isEditMode: false }

  const { lockedBy, inContextOfAppointment } = patientFileLock!
  const isStartEditingDisplayed =
    (lockedBy === userName && appointmentId !== null && appointmentId !== inContextOfAppointment?.id) || !lockedBy

  const isEditMode =
    lockedBy === userName &&
    (!appointmentId || appointmentId === inContextOfAppointment?.id) &&
    isStartEditingDisplayed === false

  return { isStartEditingDisplayed, isEditMode }
}

const PatientLayout = () => {
  const isPreview = usePreviewParam()
  const patientId = usePatientParam()
  const countryCode = useCountryParam()
  const isConcluded = useConcludeDialogStoreContext((state) => state.isConcluded)
  const removeSharedMedicalRecordNote = useSharedMedicalRecordNoteStore((state) => state.removeSharedMedicalRecordNote)

  const { userName } = useUser()

  const appointmentId = useAppointmentParam()

  const [data] = usePatientByIdQuery(patientId, countryCode, appointmentId)

  const emailVerification = {
    ...data.patientById?.emailVerification,
  }

  const countryPatient = data.patientById
  const releasePatientFileLock = useReleasePatientFileLockMutation()
  if (!countryPatient) {
    throw new Error("Patient not found")
  }
  const treatmentPlanTemplatesStore = useContext(TreatmentPlanTemplatesStoreContext)

  const patient = useFragment<PatientFragment$key>(PatientFragment, countryPatient)
  const patientName = `${patient.firstName} ${patient.lastName}`

  useEffect(() => {
    if (treatmentPlanTemplatesStore) {
      fetchTreatmentPlanTemplates(patient.country, treatmentPlanTemplatesStore)
    }
  }, [patient.country, treatmentPlanTemplatesStore])

  const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)

  const isCaveEmpty = !countryPatient.caveEntry?.message
  const hasActiveCoverage = !!countryPatient?.insurees?.find((item) => item.activeCoverages.length)
  const isDuplicate = !!countryPatient.duplicateOfPatient

  const { isStartEditingDisplayed, isEditMode } = getIsEditMode(userName, data, appointmentId, isPreview, isDuplicate)
  const previousIsEditMode = usePrevious(isEditMode)

  const closeDrawer = useDrawerStore((state) => state.closeDrawer)
  const setCloseConfirmationDialog = useMedicalRecordDialogStore((state) => state.setCloseConfirmationDialog)
  const openCloseConfirmationDialog = useMedicalRecordDialogStore((state) => state.openCloseConfirmationDialog)
  const cancelAppointmentMutation = useCancelAppointmentMutation()

  const contextAppointment = useContextAppointment()
  useMedicalReportAttachmentSubscription(patient.country, patient.id)

  const callId = useCallContextStore((state) => state.callID)
  const callLineCode = useCallContextStore((state) => state.callLineCode)
  const isChatLocation = useIsChatLocation()

  // Forcing refetch of the data by incrementing fetchKey value
  const [fetchKey, setFetchKey] = useState(0)
  const [isRefetch, setIsRefetch] = useState(isEditMode)

  useEffect(() => {
    setFetchKey((prevKey) => prevKey + 1)
    if (isEditMode) {
      setIsRefetch(true)
    }
  }, [isEditMode])

  const safeCloseWindow = () => {
    window.onerror = () => {
      console.log("error")
    }

    window.onunhandledrejection = () => {
      console.log("async/promise error")
    }

    AppInsights.appInsights.config.disableTelemetry = true
    faro.pause()

    SofiaEnvironment.IsWindowClosing = true

    window.close()
  }

  const openConfirmationDialog = () => {
    if (appointmentId && contextAppointment && !contextAppointment.isCompleted) {
      setCloseConfirmationDialog(true)
    } else {
      removeSharedMedicalRecordNote(patientId)
      window.disableConfirmBeforeLeaving = true
      releasePatientFileLock({ patientId, shouldIncreaseMissedCall: false }, safeCloseWindow)
    }
  }

  const handleCancelAppointment = (shouldIncreaseMissedCall: boolean) => {
    cancelAppointmentMutation({ id: appointmentId!, shouldIncreaseMissedCall, willRelease: true }, () =>
      setCloseConfirmationDialog(false),
    )
  }

  const handleCloseMedicalRecord = ({ decision, shouldIncreaseMissedCall }: CloseOutput) => {
    switch (decision) {
      case "KEEP":
        removeSharedMedicalRecordNote(patientId)
        releasePatientFileLock({ patientId, shouldIncreaseMissedCall: shouldIncreaseMissedCall }, () => {
          setCloseConfirmationDialog(false)

          if (!isChatLocation) {
            safeCloseWindow()
          }
        })
        break
      case "CANCEL":
        handleCancelAppointment(shouldIncreaseMissedCall)
        break
      default:
        break
    }
  }

  useEffect(() => {
    if ((isEditMode && previousIsEditMode === false) || (!isEditMode && previousIsEditMode === true)) {
      closeDrawer()
    }
  }, [closeDrawer, isEditMode, previousIsEditMode])

  const switchedFromReadToEditMode = isEditMode && previousIsEditMode === false

  const showLeaveConfirmation =
    !isConcluded &&
    !openCloseConfirmationDialog &&
    isEditMode &&
    !switchedFromReadToEditMode &&
    !isPreview &&
    !isUpdateDialogOpen

  useConfirmBeforeLeaving(showLeaveConfirmation)

  const patientEpisodesOfCareData = usePatientEpisodesOfCare(
    patientId,
    appointmentId,
    isEditMode,
    contextAppointment ? getProgressNoteKind(contextAppointment.service) : "ADMINISTRATIVE",
    contextAppointment?.language ?? "ENGLISH",
    fetchKey,
    isRefetch,
    setIsRefetch,
  )

  const recordExtractEpisodeOfCareOptions =
    patientEpisodesOfCareData.patientEpisodesOfCare?.edges
      ?.filter(({ node }) => !node.isVirtual)
      .map(({ node }) => ({ id: node.id, label: node.icdDiagnosis.text })) ?? []

  const deleteProgressNotesMutation = useDeleteProgressNotes(countryCode)
  const deleteEpisodesOfCareMutation = useDeleteEpisodesOfCare(countryCode)
  const progressNoteData = useProgressNoteData(patientEpisodesOfCareData, isEditMode)

  const { progressNoteDeleteData, progressNoteRemoveData, progressNoteAndParentData } = progressNoteData
  const previousProgressNote = progressNoteAndParentData.find(
    (i) => i.episodeOfCareProgressNoteData.followUpAppointment?.id === appointmentId,
  )?.episodeOfCareProgressNoteData
  const previousProgressNoteBillingDataRef = previousProgressNote?.appointment.billingAttribute

  const emptyEpisodesData = useEmptyEpisodesOfCareData(patientEpisodesOfCareData, patientId)
  const shouldDeleteEmptyEpisodesOfCareRef = useRef(true)

  const [shouldDeleteProgressNotes, setShouldDeleteProgressNotes] = useState(true)

  useEffect(() => {
    if (isEditMode) {
      setShouldDeleteProgressNotes(true)
    }
  }, [isEditMode])

  useEffect(() => {
    // If we are in edit mode and there are stale drafts from any user, then they should be removed
    if (isEditMode && shouldDeleteProgressNotes) {
      if (progressNoteDeleteData.length > 0) {
        const idsToDelete = progressNoteDeleteData.map((v) => v.id)
        const connectionIdsToDelete = progressNoteDeleteData.map((v) => v.connectionId)
        deleteProgressNotesMutation({ ids: idsToDelete }, connectionIdsToDelete)
      }

      setShouldDeleteProgressNotes(false)
    }

    // In both read and edit mode, we don't want to display the stale drafts. Thus, we directly remove them from the store and they will asynchronously be removed once we are in edit mode.
    // - In left sidemenu we show the number of progress notes for that episode of care, or for all progress notes
    // - The badge count show reflect what we display, thus we should unlink the progress notes that we don't display
    for (const element of progressNoteRemoveData) {
      commitLocalUpdate(RelayEnvironment, (store) => {
        const connection = store.get(element.connectionId)

        if (connection) {
          ConnectionHandler.deleteNode(connection, element.id)
        }
      })
    }
  }, [
    deleteProgressNotesMutation,
    progressNoteDeleteData,
    shouldDeleteProgressNotes,
    isEditMode,
    progressNoteRemoveData,
  ])

  useEffect(() => {
    if (isEditMode && emptyEpisodesData && shouldDeleteEmptyEpisodesOfCareRef.current) {
      const { episodeIdsToDelete, connectionId } = emptyEpisodesData

      if (episodeIdsToDelete.length > 0) {
        deleteEpisodesOfCareMutation({ ids: episodeIdsToDelete }, connectionId)
      }

      shouldDeleteEmptyEpisodesOfCareRef.current = false
    }
  }, [isEditMode, emptyEpisodesData, deleteEpisodesOfCareMutation])

  if (switchedFromReadToEditMode) {
    if (contextAppointment?.channel !== "CHAT") {
      location.reload()
    } else if (contextAppointment?.chatDetails?.chatThreadId) {
      location.replace(getChatRoute(contextAppointment.chatDetails.chatThreadId))
    }
  }

  return (
    <>
      <div className="@container/patient-layout flex h-screen w-screen flex-col">
        <UpdateVersionBanner />
        {!isPreview && !isChatLocation && <UserWarningBanner />}
        <div
          className={cn(
            "grid-rows-auto-1fr bg-background-subdued @container grid h-full overflow-hidden",
            isPreview ? "grid-rows-1fr" : "grid-rows-auto-1fr",
          )}>
          {!isPreview && (
            <div
              className="border-border bg-surface flex h-20 items-center gap-6 overflow-hidden border-b px-4 py-4 @xl:pr-8 @xl:pl-6"
              data-testid="patient-detail-head">
              <PatientHeader
                patientData={countryPatient}
                isStartEditingDisplayed={isStartEditingDisplayed}
                isEditMode={isEditMode}
                isDuplicate={isDuplicate}
                onCloseMedicalRecord={openConfirmationDialog}
                previousProgressNoteBillingDataRef={previousProgressNoteBillingDataRef}
                episodeOfCareOptions={recordExtractEpisodeOfCareOptions}
              />
            </div>
          )}

          <div className={cn("grid-rows-auto-1fr grid overflow-hidden", isPreview && "h-screen")}>
            <div className="text-body-sm grid grid-cols-1 overflow-auto">
              {!isCaveEmpty && (
                <CaveWarning
                  isEditMode={isEditMode}
                  message={countryPatient?.caveEntry?.message}
                  country={countryCode}
                />
              )}

              {callLineCode.length > 0 && <CallLineBanner variant="warning" testId="call-line-banner" />}

              {isDuplicate && <DuplicateWarningBanner primaryPatientId={countryPatient.duplicateOfPatient.id} />}

              <TeleGpOnboardingBanner
                show={
                  contextAppointment?.service === "TELE_GENERAL_PRACTITIONER" &&
                  (countryPatient?.medgateServiceInfo.isMedgateTeleGeneralPractitioner ?? false) &&
                  !(countryPatient?.teleGpOnboardingPerformed ?? false)
                }
              />
            </div>

            <div className="grid grid-cols-[88px_1fr] grid-rows-1">
              <PatientNavbar
                hasActiveCoverage={hasActiveCoverage}
                hasUpcomingPlannedAppointment={countryPatient.hasUpcomingPlannedAppointment}
              />
              <Suspense fallback={null}>
                <PointOfCareProvider
                  countryCode={countryCode}
                  hasExternalAssistance={!!countryPatient.medgateServiceInfo.externalAssistanceServiceInfo?.name}>
                  <PatientLayoutProvider countryPatient={countryPatient}>
                    <Outlet
                      context={{
                        patient,
                        isEditMode,
                        patientEpisodesOfCareData,
                        setIsUpdateDialogOpen,
                        ...countryPatient,
                        ...emailVerification,
                      }}
                    />
                  </PatientLayoutProvider>
                </PointOfCareProvider>
              </Suspense>
            </div>
          </div>
        </div>
      </div>
      {callId && <Notes />}
      <SharedMedicalRecordNote />
      <RequestTakeOverDialog patientId={patientId} patientName={patientName} />
      <CloseMedialRecordConfirmationDialog onCloseMedicalRecord={handleCloseMedicalRecord} />
      <PhoneCallTranscriptionBarContainer />
    </>
  )
}

const PatientLayoutWithCoreProviders = () => {
  return (
    <PatientFileProvider>
      <GeneralAttestationProvider>
        <LegalGuardianAttestationDrawerProvider>
          <ChMedicationProvider>
            <DeMedicationProvider>
              <ChPrescriptionStoreProvider>
                <DePrescriptionStoreProvider>
                  <ReferralStoreProvider>
                    <ConsultationReportStoreProvider>
                      <ConcludeDialogStoreProvider>
                        <ConfirmDialogStoreProvider>
                          <EditEpisodeOfCareDialogStoreProvider>
                            <EpisodeOfCareStoreProvider>
                              <PsychologicalPsytherapyStoreProvider>
                                <TherapyDrawerStoreProvider>
                                  <TreatmentPlanTemplatesStoreProvider>
                                    <BetterDocSuggestionsStoreProvider>
                                      <CareProviderManualAddressStoreProvider>
                                        <HealthInformationCopilotProvider>
                                          <PhoneCallTranscriptionStoreProvider>
                                            {/* We do not want to have global store anymore. */}
                                            {/* We're turning most of the global stores into context-based stores. */}
                                            <PatientLayout />
                                          </PhoneCallTranscriptionStoreProvider>
                                        </HealthInformationCopilotProvider>
                                      </CareProviderManualAddressStoreProvider>
                                    </BetterDocSuggestionsStoreProvider>
                                  </TreatmentPlanTemplatesStoreProvider>
                                </TherapyDrawerStoreProvider>
                              </PsychologicalPsytherapyStoreProvider>
                            </EpisodeOfCareStoreProvider>
                          </EditEpisodeOfCareDialogStoreProvider>
                        </ConfirmDialogStoreProvider>
                      </ConcludeDialogStoreProvider>
                    </ConsultationReportStoreProvider>
                  </ReferralStoreProvider>
                </DePrescriptionStoreProvider>
              </ChPrescriptionStoreProvider>
            </DeMedicationProvider>
          </ChMedicationProvider>
        </LegalGuardianAttestationDrawerProvider>
      </GeneralAttestationProvider>
    </PatientFileProvider>
  )
}

export const PatientLayoutIndependent = () => {
  const { patientId, country } = useParams()

  const upperCaseCountryCode = country?.toUpperCase() as Country

  const [searchParams] = useSearchParams()

  const appointmentId = getInsensitiveSearchParams(searchParams, "appointmentId").value

  if (!patientId) {
    throw "The patient id must be a valid value: non null, not undefined, not empty"
  }

  if (!upperCaseCountryCode) {
    throw new Error("The given string does not match any country code")
  }

  return (
    <PatientParamProvider country={upperCaseCountryCode} patientId={patientId} appointmentId={appointmentId}>
      <PreventionProgramsProvider>
        <ProgressNoteFormProvider>
          <PatientLayoutWithCoreProviders />
        </ProgressNoteFormProvider>
      </PreventionProgramsProvider>
    </PatientParamProvider>
  )
}

type PatientLayoutInChatProps = { patientId: string; appointmentId: string; country: Country }
export const PatientLayoutInChat = ({ appointmentId, country, patientId }: PatientLayoutInChatProps) => {
  return (
    <PatientParamProvider country={country} patientId={patientId} appointmentId={appointmentId}>
      <PatientLayoutWithCoreProviders />
    </PatientParamProvider>
  )
}
